import type { Context } from '../integrations/context-types';
import type { LayoutMode, PaneId, ShellState } from './shell-state';
import {
  derivedFingerprint,
  userContextFingerprint,
} from './context-fingerprints';

export const selectAgents = (s: ShellState) => s.agents;

export const selectAgentById = (s: ShellState, id: string) => s.agents.byId[id];

export const selectSelection = (s: ShellState) => s.selection;

export const selectDrawer = (s: ShellState) => s.drawer;

export const selectLauncher = (s: ShellState) => s.launcher;

export const selectActivePane = (s: ShellState, paneId: PaneId) =>
  s.launcher.panes[paneId];

export const selectContexts = (s: ShellState) => s.contexts;

/** User-attached contexts come first so the agent sees them as the
 *  most recent intent; derived contexts trail as ambient view info. */
export const selectMergedContexts = (s: ShellState): Context[] => [
  ...s.contexts.user,
  ...s.contexts.derived,
];

/** Derived contexts the host offered for the current route that the user
 *  dismissed there — the ones that can be re-attached. A chip the user
 *  promoted by pinning is dismissed from the derived lane too, but it is
 *  still attached as a user context, so it is not offered back. */
export const selectDismissedDerived = (
  s: Pick<ShellState, 'contexts'>
): Context[] => {
  const sig = s.contexts.lastDerivedRouteSignature;
  const dismissed = sig ? s.contexts.derivedDismissed?.[sig] : undefined;
  if (!dismissed?.length) return [];
  const gone = new Set(dismissed);
  const attached = new Set([
    ...s.contexts.user.map(userContextFingerprint),
    ...s.contexts.derived.map(derivedFingerprint),
  ]);
  return (s.contexts.derivedOffered ?? []).filter((c) => {
    const fp = derivedFingerprint(c);
    return gone.has(fp) && !attached.has(fp);
  });
};

export const selectError = (s: ShellState, conversationKey: string) =>
  s.errors[conversationKey];

export const selectStorageHydrated = (s: ShellState): boolean =>
  s.storageHydrated;

export const selectPendingReplay = (s: ShellState): string | null =>
  s.pendingReplay;

export const selectLayoutMode = (s: ShellState): LayoutMode => s.layoutMode;
